import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Group, Layout, Page, Tab } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, usePage, useStore } from "@/comn/hooks";
import { APIS, SF_ORG_MGMT_INFO, SF_ORG_DEPT_INFO, URLS } from "@/tra/tancis/ptli/usr/org/services/PtliOrgService";

/*
 * registration for Organization Management
 * !== 기관 관리 등록 ==!
 */
export const PTLI0916002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        orgInfo: useForm({
            defaultSchema: SF_ORG_MGMT_INFO,
            defaultValues: {},
        }),
        // Organization Information Form !== 기관의 부서 정보 form ==!
        deptInfo: useForm({
            defaultSchema: SF_ORG_DEPT_INFO,
            defaultValues: {},
        }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "L_ORG" }, { label: "L_ORG_DEPT" }],
        },
    });

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Organization Information !== 기관 정보 등록 ==!
        createOrgInfo: useFetch({
            api: (data: any) => APIS.createOrgInfo(data),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00058",
                    onConfirm: () => {
                        navigate(URLS.ptli0916001q);
                    },
                    close: false,
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        // Check Organization Information !== 기관 정보 조회 ==!
        getOrgInfo: useFetch({
            api: (data) => APIS.getOrgInfo(form.orgInfo.getValue("orgCd")),
            onSuccess: (data) => {
                const orgInfo = data.orgInfo.content;
                form.orgInfo.setValues(orgInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),

        //Organization Tin duplicate check !== 기관 TIN 중복 체크 ==!
        getOrgTinDuplCheck: useFetch({
            api: (tin: string) => APIS.getOrgTinDuplCheck(tin),
            enabled: false,
            onError: () => {
                toast.showToast({ type: "error", content: "msg.com.00018" });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        initDeptForm: () => {
            form.deptInfo.clearValues();
            form.deptInfo.setValue("deptPath", "ROOT");
        },
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => {
            navigate(URLS.ptli0916001q);
        },
        // Click Organization Save Button !== 기관 저장 버튼 클릭 ==!
        onOrgSaveBtnClick: async () => {
            //fetch.getOrgInfo.fetch();
            const regex = /^\d{9}$/; // TIN을 체크하는 정규식 1. 숫자로만 이루어짐, 2. 9자리수로 이루어져야함
            const data = form.orgInfo.getValues();
            const tin = form.orgInfo.getValue("tin");

            // 정규화를 통한 TIN 조건 처리
            if (!regex.test(tin)) {
                modal.openModal({
                    content: "msg.esw.00070", // TIN must be 9 digits. ? !== TIN은 9자리여야 합니다. ? ==!
                });
                return;
            }

            // TIN 상태 조회: null(사용 가능) | { orgCd, tin, delYn, ... }
            const resp = await APIS.getOrgTinDuplAndDelCheck(tin);
            const orgTinInfo = resp.data.orgTinDuplCheck.content;

            if (orgTinInfo) {
                // if (orgTinInfo.delYn === "Y") {
                //     // 삭제된 기관이 이 TIN을 보유 중 -> 복원 여부를 확인
                //     modal.openModal({
                //         content: t("msg.cgm.00219", {
                //             0: t(
                //                 "이 TIN으로 등록된 기관이 존재하지만 삭제된 상태입니다. 해당 기관을 복원하시겠습니까?",
                //             ),
                //         }),
                //         onConfirm: async () => {
                //             try {
                //                 await APIS.restoreOrgInfo(orgTinInfo.orgCd);
                //                 modal.openModal({
                //                     content: "msg.com.00058", // Successfully processed. !== 정상적으로 처리되었습니다. ==!
                //                     onConfirm: () => {
                //                         navigate(URLS.ptli0916001q);
                //                     },
                //                     close: false,
                //                 });
                //             } catch {
                //                 toast.showToast({ type: "error", content: "msg.com.00018" });
                //             }
                //         },
                //     });
                //     return;
                // }

                if (orgTinInfo.delYn === "Y") {
                    modal.openModal({
                        content: t("msg.ptl.00855", {
                            0: "이 TIN으로 등록된 기관이 존재하지만 삭제된 상태입니다. 해당 기관을 복원하시겠습니까?",
                        }),
                        onConfirm: async () => {
                            try {
                                await APIS.restoreOrgInfo(orgTinInfo.orgCd);

                                modal.openModal({
                                    content: "msg.com.00058",
                                    onConfirm: () => {
                                        navigate(URLS.ptli0916001q);
                                    },
                                    close: false,
                                });
                            } catch {
                                toast.showToast({
                                    type: "error",
                                    content: "msg.com.00018",
                                });
                            }
                        },
                    });

                    return;
                }

                // 활성 기관이 이미 이 TIN을 사용 중
                modal.openModal({ content: "msg.ptl.00770" }); // This TIN number is already in use. ? !== 이미 사용 중인 TIN 번호입니다. ? ==!
                return;
            }

            // TIN 사용 가능 -> 정상 등록 진행
            form.orgInfo.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createOrgInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Click Department Tab 부서 탭 클릭
        onDeptTabClick: (index: any) => {
            form.deptInfo.setEditable(false);
            if (index === 1) {
                modal.openModal({
                    content: t("msg.cgm.00219", {
                        0: t("저장하지 않은 데이터가 존재합니다. 먼저, 저장하고 다시 시도해주시기 바랍니다."),
                    }),
                });
                tab.setActive(0);
                return;
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        form.deptInfo.setEditable(true);
        handler.initDeptForm();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.orgRegnDvnCd}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.orgAbrtNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.orgNm}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.tin}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.hgovNm}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.orgStatCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.rprsTelno}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.rprsFaxNo}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.lpcoIssuYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.postAddr}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onOrgSaveBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};